export type DeliveryType =
  | "trigger_sync"
  | "ai_decisioning"
  | "esp_orchestrated";
export type DestinationType =
  | "iterable"
  | "braze"
  | "sfmc"
  | "klaviyo"
  | "sailthru"
  | "xtremepush";

export const DESTINATION_TYPE_LABEL: Record<DestinationType, string> = {
  iterable: "Iterable",
  braze: "Braze",
  sfmc: "Salesforce Marketing Cloud",
  klaviyo: "Klaviyo",
  sailthru: "Sailthru",
  xtremepush: "Xtremepush",
};

/** Every ESP the compiler can emit an esp-profile template for. */
export const ESP_DESTINATION_TYPES = Object.keys(
  DESTINATION_TYPE_LABEL,
) as DestinationType[];

export const getDestinationType = (
  type: string | undefined,
): DestinationType | null => {
  return type === "iterable" ||
    type === "braze" ||
    type === "sfmc" ||
    type === "klaviyo" ||
    type === "sailthru" ||
    type === "xtremepush"
    ? type
    : null;
};

export const getAvailableCompileDestinationId = ({
  compileDestinationId,
  availableDestinationIds,
}: {
  compileDestinationId: string | null;
  availableDestinationIds: string[];
}): string | null =>
  compileDestinationId && availableDestinationIds.includes(compileDestinationId)
    ? compileDestinationId
    : null;

/** ESP types that can host an AID email channel for dynamic content. */
export const isAidDestinationType = (
  type: string | null | undefined,
): type is "iterable" | "sfmc" => type === "iterable" || type === "sfmc";

/** Warehouse-managed triggered send (assignment model + sync). */
export const isTriggerSyncDestinationType = (
  type: string | null | undefined,
): type is "iterable" | "braze" | "sfmc" =>
  type === "iterable" || type === "braze" || type === "sfmc";

/**
 * Destinations that can host an SMS/push Launch deploy (not the copy-editor
 * export path). SMS is Iterable only; push is Iterable + SFMC.
 */
export const isMessageDeployDestinationType = (
  type: string | null | undefined,
  channel: "sms" | "push",
): type is "iterable" | "sfmc" => {
  if (channel === "sms") return type === "iterable";
  return type === "iterable" || type === "sfmc";
};

/** ESP-orchestrated: template-only deploy with ESP-native profile predicates. */
export const isEspOrchestratedDestinationType = (
  type: string | null | undefined,
): type is DestinationType =>
  type === "braze" ||
  type === "iterable" ||
  type === "sfmc" ||
  type === "klaviyo" ||
  type === "sailthru" ||
  type === "xtremepush";

/**
 * SMS/push compile for the copy editor, whose schema enum is narrower than the
 * email one. Xtremepush is excluded because our integration implements
 * only the email campaign methods, not because XP doesn't support SMS/push: it
 * sends SMS and push from the same create/campaign endpoint under other
 * `messages` type ids (9 is SMS, 1/2/3 push).
 */
export const getMessageCompileTarget = (
  target: DestinationType | null,
): Exclude<DestinationType, "xtremepush"> | null =>
  target === "xtremepush" ? null : target;

export const getCompileTarget = ({
  deliveryType,
  destinationType,
}: {
  deliveryType: DeliveryType;
  destinationType?: DestinationType | null;
}): DestinationType | null => {
  if (deliveryType === "esp_orchestrated") {
    return isEspOrchestratedDestinationType(destinationType)
      ? destinationType
      : null;
  }
  if (deliveryType === "ai_decisioning") {
    // AID compiles for the channel's ESP. Braze is not supported.
    return isAidDestinationType(destinationType) ? destinationType : null;
  }
  return isTriggerSyncDestinationType(destinationType) ? destinationType : null;
};

export const getIterableCampaignUrl = (campaignId: string): string =>
  `https://app.iterable.com/campaigns/${encodeURIComponent(campaignId)}`;

export const getAidMessageUrl = ({
  flowId,
  messageId,
}: {
  flowId: string;
  messageId: string;
}): string =>
  `/ai/flows/${encodeURIComponent(flowId)}/messages/${encodeURIComponent(messageId)}`;

/** Pull flow/message ids from a deployment's target_refs.aid journal. */
export const getAidMessageUrlFromTargetRefs = (
  targetRefs: unknown,
): string | null => {
  if (!targetRefs || typeof targetRefs !== "object") return null;
  const aid = (targetRefs as { aid?: unknown }).aid;
  if (!aid || typeof aid !== "object") return null;
  const { flowId, messageId } = aid as {
    flowId?: unknown;
    messageId?: unknown;
  };
  if (typeof flowId !== "string" || typeof messageId !== "string") return null;
  if (!flowId || !messageId) return null;
  return getAidMessageUrl({ flowId, messageId });
};

export const getDeployedAidMessageUrl = (
  deployments:
    | Array<{ delivery_type: string; target_refs?: unknown }>
    | null
    | undefined,
): string | null => {
  const aidDeployment = deployments?.find(
    (d) => d.delivery_type === "ai_decisioning",
  );
  return getAidMessageUrlFromTargetRefs(aidDeployment?.target_refs) ?? null;
};

/**
 * Deep link to the Iterable campaign a triggered send created, if any.
 *
 * Iterable campaigns are created Ready rather than Running on purpose, so
 * delivery and compliance settings get reviewed first — but `/email/target`
 * (what the managed sync calls) only accepts sends once the campaign is
 * Running. The review and the activation both happen in Iterable, so the
 * useful thing to offer is the way there.
 *
 * Iterable-only: SFMC's console URL is a generic Content Builder landing page
 * rather than a link to the asset, and Braze's needs the destination's region
 * and still only lands on a filtered campaign list.
 */
export const getDeployedIterableCampaignUrl = (
  deployments:
    | Array<{
        target_type: string;
        delivery_type: string;
        target_refs?: unknown;
      }>
    | null
    | undefined,
): string | null => {
  for (const deployment of deployments ?? []) {
    if (
      deployment.target_type !== "iterable" ||
      deployment.delivery_type !== "trigger_sync" ||
      !deployment.target_refs ||
      typeof deployment.target_refs !== "object"
    ) {
      continue;
    }
    const campaignId = (deployment.target_refs as { campaignId?: unknown })
      .campaignId;
    if (typeof campaignId === "string" && campaignId) {
      return getIterableCampaignUrl(campaignId);
    }
  }
  return null;
};

export const getDeployedTemplateId = (
  deployments:
    | Array<{
        destination_id: string | number;
        target_refs?: unknown;
      }>
    | null
    | undefined,
  destinationId: string | null,
): string | null => {
  if (!destinationId) return null;
  for (const deployment of deployments ?? []) {
    if (
      String(deployment.destination_id) !== destinationId ||
      !deployment.target_refs ||
      typeof deployment.target_refs !== "object"
    ) {
      continue;
    }
    const templateId = (deployment.target_refs as { templateId?: unknown })
      .templateId;
    if (typeof templateId === "string" && templateId) return templateId;
    if (typeof templateId === "number" && Number.isFinite(templateId)) {
      return String(templateId);
    }
  }
  return null;
};

/**
 * Destination to preselect when reopening export/deploy: prefer a journaled
 * prior export whose destination is still in the picker, so a manual Iterable
 * choice survives even when Context Hub's ESP default is unset or different.
 */
export const getPriorExportDestinationId = ({
  deployments,
  availableDestinationIds,
}: {
  deployments:
    | Array<{
        destination_id: string | number;
        target_refs?: unknown;
      }>
    | null
    | undefined;
  availableDestinationIds: Iterable<string>;
}): string | null => {
  const available = new Set(
    [...availableDestinationIds].map((id) => String(id)),
  );
  if (available.size === 0) return null;

  // Prefer a row that journals a template id — that's a completed ESP export.
  for (const deployment of deployments ?? []) {
    const destinationId = String(deployment.destination_id);
    if (!available.has(destinationId)) continue;
    if (getDeployedTemplateId([deployment], destinationId)) {
      return destinationId;
    }
  }
  for (const deployment of deployments ?? []) {
    const destinationId = String(deployment.destination_id);
    if (available.has(destinationId)) return destinationId;
  }
  return null;
};

export const getForceRecreateCampaignInput = (
  forceRecreateCampaign: boolean,
): { forceRecreateCampaign: true } | Record<string, never> =>
  forceRecreateCampaign ? { forceRecreateCampaign: true } : {};

/** Braze deploy extras for template upsert / campaign attach.
 * Campaign selection is a second step after the template exists.
 * Pass an empty recipientColumn when there is no HT sync (ESP-orchestrated). */
export const getBrazeDeployInput = ({
  isBraze,
  recipientColumn,
  campaignId,
}: {
  isBraze: boolean;
  recipientColumn: string;
  campaignId?: string;
}):
  | {
      recipientColumn?: string;
      campaignId?: string;
    }
  | Record<string, never> => {
  if (!isBraze) return {};
  return {
    ...(recipientColumn ? { recipientColumn } : {}),
    ...(campaignId ? { campaignId } : {}),
  };
};

/** Persisted Braze journal on content_deployments.target_refs. */
export type BrazeDeploymentRefs = {
  templateId: string;
  campaignId: string | null;
  recipientColumn: string | null;
  syncId: string | null;
  deploymentId: string;
};

/** Restore Braze template/campaign state from a prior Braze deployment
 * (trigger_sync or esp_orchestrated) so the modal can skip template creation
 * and only attach a campaign. */
export const getBrazeDeploymentRefs = (deployment: {
  id: string;
  sync_id?: string | number | null;
  delivery_type: string;
  target_refs?: unknown;
}): BrazeDeploymentRefs | null => {
  if (
    deployment.delivery_type !== "trigger_sync" &&
    deployment.delivery_type !== "esp_orchestrated"
  ) {
    return null;
  }
  if (!deployment.target_refs || typeof deployment.target_refs !== "object") {
    return null;
  }
  const refs = deployment.target_refs as {
    templateId?: unknown;
    campaignId?: unknown;
    braze?: { recipientColumn?: unknown };
  };
  if (typeof refs.templateId !== "string" || !refs.templateId) return null;
  const recipientColumn = refs.braze?.recipientColumn;
  return {
    templateId: refs.templateId,
    campaignId: typeof refs.campaignId === "string" ? refs.campaignId : null,
    recipientColumn:
      typeof recipientColumn === "string" && recipientColumn
        ? recipientColumn
        : null,
    syncId:
      deployment.sync_id != null && deployment.sync_id !== ""
        ? String(deployment.sync_id)
        : null,
    deploymentId: deployment.id,
  };
};

/** One deployment row per (email, destination). Switching delivery type
 * clears the prior type's target_refs/sync — the API requires an explicit
 * forceReplaceDeployment flag for that. */
export const needsDeliveryTypeReplace = ({
  existingDeliveryType,
  deliveryType,
}: {
  existingDeliveryType: string | null | undefined;
  deliveryType: DeliveryType;
}): boolean =>
  Boolean(existingDeliveryType && existingDeliveryType !== deliveryType);

export const getForceReplaceDeploymentInput = (
  forceReplaceDeployment: boolean,
): { forceReplaceDeployment: true } | Record<string, never> =>
  forceReplaceDeployment ? { forceReplaceDeployment: true } : {};

export const getIterableMessageTypeInput = ({
  isIterable,
  messageTypeId,
}: {
  isIterable: boolean;
  messageTypeId: string | null;
}): { messageTypeId: string } | Record<string, never> =>
  isIterable && messageTypeId ? { messageTypeId } : {};

/** A UTM/link parameter appended to every link in a deployed template. */
export type TemplateLinkParam = {
  key: string;
  value: string;
};

/**
 * Iterable's template settings, for the two surfaces that deploy an email to
 * it. Only Iterable applies them, and only ESP-orchestrated delivery reaches
 * the code that does — the API rejects them anywhere else.
 *
 * `hasEdits` carries the weight. A present `linkParams` — an empty list
 * included — tells the API "these are the params", replacing whatever the
 * source email carried; absent means "inherit". So an editor the user has not
 * touched must send nothing, or the first paint's empty state posts as a clear.
 * Explicitness is per-request rather than per-field, which is why the two
 * settings travel together: sending only the campaign name would drop the link
 * params with it.
 */
export const getIterableTemplateOptionsInput = ({
  isIterable,
  hasEdits,
  linkParams,
  googleAnalyticsCampaignName,
  createCampaign,
}: {
  isIterable: boolean;
  hasEdits: boolean;
  linkParams: TemplateLinkParam[];
  googleAnalyticsCampaignName: string;
  createCampaign: boolean;
}): {
  linkParams?: TemplateLinkParam[];
  googleAnalyticsCampaignName?: string;
  createCampaign?: true;
} => {
  if (!isIterable) return {};
  return {
    ...(hasEdits && {
      // Blank keys are rows the user started and left empty, not params.
      linkParams: linkParams.filter((param) => param.key.trim().length > 0),
      googleAnalyticsCampaignName: googleAnalyticsCampaignName.trim(),
    }),
    ...(createCampaign && { createCampaign: true }),
  };
};

/** SFMC trigger_sync extras. Omit blank fields so redeploys can reuse
 * values persisted on the deployment's target_refs. */
export const getSfmcDeployInput = ({
  isSfmc,
  recipientColumn,
  sendClassificationId,
}: {
  isSfmc: boolean;
  recipientColumn: string;
  sendClassificationId: string;
}):
  | {
      recipientColumn?: string;
      sendClassificationId?: string;
    }
  | Record<string, never> => {
  if (!isSfmc) return {};
  return {
    ...(recipientColumn ? { recipientColumn } : {}),
    ...(sendClassificationId ? { sendClassificationId } : {}),
  };
};

/** SFMC push extras. Omit blank fields so redeploys can reuse
 * values persisted on the deployment's target_refs. */
export const getSfmcPushDeployInput = ({
  isSfmc,
  recipientColumn,
  applicationId,
  applicationName,
}: {
  isSfmc: boolean;
  recipientColumn: string;
  applicationId: string;
  applicationName: string;
}):
  | {
      recipientColumn?: string;
      applicationId?: string;
      applicationName?: string;
    }
  | Record<string, never> => {
  if (!isSfmc) return {};
  return {
    ...(recipientColumn ? { recipientColumn } : {}),
    ...(applicationId ? { applicationId } : {}),
    ...(applicationName ? { applicationName } : {}),
  };
};
